import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import axios from "axios";
import MDSpinner from "react-md-spinner";
import { FaSatelliteDish, FaTachometerAlt } from "react-icons/fa";
import { toast } from "react-toastify";

import myConfig from "../../configs/config";
import MapContainer from "../../components/Polygon_map";
import LineGraph from "../Operation/LineGraph";
import CardContent from "../../components/CardContent";
import FieldAppTimezoneToggle from "./FieldAppTimezoneToggle";
import {
  formatFieldAppDateTime,
  readTrackingTimezoneMode,
  writeTrackingTimezoneMode,
} from "./fieldAppTimezoneDisplay";
import {
  fetchTrackingProgressive,
  hasDisplayableTracking,
  publishedTrackingSnapshot,
} from "./fieldAppTrackingLoader";
import { mergeTrackingPayloads } from "./fieldAppTrackingMerge";
import {
  buildTrackingCacheKey,
  clearCachedTracking,
  getCachedTracking,
  setCachedTracking,
} from "./fieldAppTrackingCache";
import {
  buildCalendarDayRanges,
  buildGraphDayRanges,
  fuelStatsForRange,
  getDaysWithTracking,
  applyChartDayClick,
} from "./FieldAppActivityTimeline";
import {
  allPeriodDaysSelected,
  buildSingleOperatorSelection,
  dayKeyFromIso,
  selectionTimeBoundsMs,
} from "./fieldAppPeriodUtils";
import {
  fetchLiveEquipmentPosition,
  isTodayVisibleInTracking,
  LIVE_EQUIPMENT_POLL_MS,
} from "./fieldAppLiveEquipment";
import FieldAppActivitySessionDetail from "./FieldAppActivitySessionDetail";
import FieldAppMachineSchedulePanel from "./FieldAppMachineSchedulePanel";
import { fetchMachineSchedule } from "./fieldAppApi";
import { fetchGroupFarms } from "../PendingSchedules/serviceOrderLocationApi";
import { landPlotIdsForSubFarm } from "../../utils/mapMeasurements";
import {
  buildDisplayActivityLanes,
  machinesActiveInGraph,
  telemetryBoundaryMarkers,
} from "./GraphActivityLanesLayer";
import FieldAppTrackingWeatherHud from "./FieldAppTrackingWeatherHud";
import {
  loadWeatherInfoWindowHtml,
  pickWeatherFallbackSeries,
  pickWeatherSeries,
  weatherLoadingHtml,
  weatherTimeMs,
  WEATHER_LOOKUP_MAX_MS,
} from "./fieldAppWeather";
import {
  POINT_DETAILS_INFO_WINDOW_MS,
  pointDetailsInfoWindowHtml,
} from "./pointDetailsInfoWindow";
import { formatRelativeTime } from "../../helpers/utils";
import {
  combineOperatorTrailLines,
  extractRawPathPoints,
  rebuildTrackingLines,
  stoppedPointsFromLines,
  buildGraphSeriesFromLines,
  lastValidPointFromLines,
  resolveOperatorMapEquipment,
} from "./fieldAppTrackingUtils";
import {
  calculatePeriodMotionStats,
  calculatePathDistanceStats,
  estimateWorkedAreaHa,
  formatPeriodClockDuration,
} from "../../helpers/operationPeriodStats";
import FieldAppServiceOrdersSection, {
  focusGraphIndicesForPeriod,
} from "./FieldAppServiceOrdersSection";
import "../Operation/Operation.css";
import "./FieldAppHome.css";

const DEFAULT_WORKING_WIDTH_M = 12;
const MAP_HEIGHT_STORAGE_KEY = "field-app-tracking-map-height";
const MAP_HEIGHT_MIN = 260;
const MAP_HEIGHT_MAX = 780;
const MAP_HEIGHT_DEFAULT = 480;
const GRAPH_CLICK_ZOOM = 16;
/** Zoom ~100 m na barra de escala do Google Maps (ponto de coleta / amostra). */
const SAMPLE_POINT_MAP_ZOOM = 16;
/** Zoom do popup de telemetria ao clicar no gráfico (trecho do operador). */
const TELEMETRY_POPUP_MAP_ZOOM = GRAPH_CLICK_ZOOM;
const MAP_ZOOM_MIN = 10;
const MAP_ZOOM_MAX = 21;
const PLANNED_SPEED_STORAGE_PREFIX = "field-app-planned-speed-";
const PLANNED_SPEED_MIN = 1;
const PLANNED_SPEED_MAX = 120;
const PLANNED_SPEED_DEFAULT = 15;

function readStoredMapHeight() {
  try {
    const raw = localStorage.getItem(MAP_HEIGHT_STORAGE_KEY);
    const parsed = Number(raw);
    if (parsed >= MAP_HEIGHT_MIN && parsed <= MAP_HEIGHT_MAX) {
      return parsed;
    }
  } catch (error) {
    /* ignore */
  }
  return MAP_HEIGHT_DEFAULT;
}

function clampPlannedSpeed(value) {
  return Math.min(
    PLANNED_SPEED_MAX,
    Math.max(PLANNED_SPEED_MIN, value)
  );
}

function readStoredPlannedSpeed(farmId) {
  if (!farmId) return null;
  try {
    const raw = localStorage.getItem(`${PLANNED_SPEED_STORAGE_PREFIX}${farmId}`);
    const parsed = Number(raw);
    if (parsed >= PLANNED_SPEED_MIN && parsed <= PLANNED_SPEED_MAX) {
      return parsed;
    }
  } catch (error) {
    /* ignore */
  }
  return null;
}

function writeStoredPlannedSpeed(farmId, value) {
  if (!farmId) return;
  try {
    localStorage.setItem(
      `${PLANNED_SPEED_STORAGE_PREFIX}${farmId}`,
      String(clampPlannedSpeed(value))
    );
  } catch (error) {
    /* ignore */
  }
}

function initialPlannedSpeed(farmId) {
  const stored = readStoredPlannedSpeed(farmId);
  return stored != null ? stored : PLANNED_SPEED_DEFAULT;
}

function buildGraphSeries(mapData) {
  if (!mapData || !mapData.lines || !mapData.lines.length) return [];
  const values = mapData.lines.reduce(
    (acc, line) => acc.concat(line.path || []),
    []
  );
  return values.map(({ time, speed }) => ({
    velocidade: Number(String(speed).split(" km/h")[0]) || 0,
    time: new Date(time).getTime(),
    timestamp: time,
  }));
}

function buildFlatLineArray(mapData) {
  if (!mapData || !mapData.lines) return [];
  return mapData.lines.reduce(
    (acc, { path, color }) => acc.concat((path || []).map((point) => ({ ...point, color }))),
    []
  );
}


function filterPathPointsByTimeRange(points, startTimeMs, endTimeMs) {
  if (!points.length || startTimeMs == null || endTimeMs == null) {
    return points;
  }
  return points.filter((point) => {
    const ms = new Date(point.time).getTime();
    return !Number.isNaN(ms) && ms >= startTimeMs && ms <= endTimeMs;
  });
}

function graphIndicesForTimeRange(graphData, startTimeMs, endTimeMs) {
  if (!graphData.length || startTimeMs == null || endTimeMs == null) {
    return { startIndex: 0, endIndex: Math.max(graphData.length - 1, 0) };
  }

  let startIndex = 0;
  let endIndex = graphData.length - 1;

  for (let index = 0; index < graphData.length; index += 1) {
    if (graphData[index].time >= startTimeMs) {
      startIndex = index;
      break;
    }
  }

  for (let index = graphData.length - 1; index >= 0; index -= 1) {
    if (graphData[index].time <= endTimeMs) {
      endIndex = index;
      break;
    }
  }

  if (endIndex < startIndex) {
    endIndex = startIndex;
  }

  return { startIndex, endIndex };
}

function sliceVisiblePathPoints(flatPoints, graphBrushRange, graphData) {
  if (!flatPoints.length || !graphData.length) return [];

  const { startIndex, endIndex } = graphBrushRange;
  const safeEnd =
    endIndex == null || endIndex >= graphData.length
      ? graphData.length - 1
      : endIndex;
  const startPoint = graphData[startIndex];
  const endPoint = graphData[safeEnd];
  const startTs = startPoint && startPoint.timestamp;
  const endTs = endPoint && endPoint.timestamp;

  if (startTs && endTs) {
    const startIdx = flatPoints.findIndex((point) => point.time === startTs);
    const endIdx = flatPoints.findIndex((point) => point.time === endTs);
    if (startIdx !== -1 && endIdx !== -1) {
      return flatPoints.slice(startIdx, endIdx + 1);
    }
  }

  return flatPoints.slice(startIndex, safeEnd + 1);
}

export default function FieldAppTrackingPanel({
  farmId,
  selection,
  chartSelection = null,
  selectedDayKeys = [],
  onSelectedDayKeysChange,
  periodDayKeys = [],
  machines = [],
  active,
  refreshToken,
  focusSessionUuid = null,
  onFocusSessionHandled,
  focusGraphPeriod = null,
  onFocusGraphPeriodHandled,
  initialMapCenter = null,
  mapFullDayPath = false,
  highlightMapPoint = null,
  embedded = false,
  combineTrails = false,
  t,
}) {
  const displaySelection = chartSelection || selection;
  const loadSelection = selection;
  const queryTimeBounds = useMemo(
    () => selectionTimeBoundsMs(displaySelection),
    [displaySelection]
  );
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(null);
  const [loadedFromCache, setLoadedFromCache] = useState(false);
  const [tracking, setTracking] = useState(null);
  const [liveEquipment, setLiveEquipment] = useState(null);
  const [landplots, setLandplots] = useState([]);
  const [groupFarms, setGroupFarms] = useState([]);
  const [graphBrushRange, setGraphBrushRange] = useState({
    startIndex: 0,
    endIndex: null,
  });
  const [plannedSpeedKmh, setPlannedSpeedKmh] = useState(() =>
    initialPlannedSpeed(farmId)
  );
  const [speedEditorOpen, setSpeedEditorOpen] = useState(false);
  const [speedDraft, setSpeedDraft] = useState(() =>
    String(initialPlannedSpeed(farmId))
  );
  const [focusBrushRange, setFocusBrushRange] = useState(null);
  const daySelectionReady = Boolean(selectedDayKeys && selectedDayKeys.length);
  const [visibleGraphRange, setVisibleGraphRange] = useState({
    startTimeMs: null,
    endTimeMs: null,
  });
  const [selectedActivityInterval, setSelectedActivityInterval] = useState(null);
  const [closureChartPickActive, setClosureChartPickActive] = useState(false);
  const [closureChartPoint, setClosureChartPoint] = useState(null);
  const [machineScheduleExpanded, setMachineScheduleExpanded] = useState(true);
  const [scheduleMachinePick, setScheduleMachinePick] = useState("");
  const [machineScheduleRows, setMachineScheduleRows] = useState([]);
  const [machineScheduleMeterType, setMachineScheduleMeterType] =
    useState("hour_meter");
  const [machineScheduleLoading, setMachineScheduleLoading] = useState(false);
  const [machineScheduleError, setMachineScheduleError] = useState(null);
  const [machineScheduleLandplotId, setMachineScheduleLandplotId] = useState(null);
  const [machineScheduleLandplotName, setMachineScheduleLandplotName] =
    useState("");
  const [machineScheduleSubFarmId, setMachineScheduleSubFarmId] = useState(null);
  const graphMarkerRef = useRef(null);
  const mapSectionRef = useRef(null);
  const [mapHeightPx, setMapHeightPx] = useState(readStoredMapHeight);
  const mapHeightRef = useRef(mapHeightPx);
  const mapResizeRef = useRef({ active: false, startY: 0, startHeight: 0 });
  const lastRefreshTokenRef = useRef(refreshToken);
  const loadGenerationRef = useRef(0);
  const mapInteractionZoomRef = useRef(null);
  const chartFocusKeyRef = useRef("");
  const trackingRef = useRef(null);
  trackingRef.current = tracking;
  const visibleCacheKeyRef = useRef("");
  const [directionMarkerClearToken, setDirectionMarkerClearToken] = useState(0);
  const [selectedTrackTimeMs, setSelectedTrackTimeMs] = useState(null);
  const [timezoneMode, setTimezoneMode] = useState(() =>
    readTrackingTimezoneMode(farmId)
  );
  mapHeightRef.current = mapHeightPx;

  useEffect(() => {
    setTimezoneMode(readTrackingTimezoneMode(farmId));
  }, [farmId]);

  const handleTimezoneModeChange = useCallback(
    (mode) => {
      setTimezoneMode(mode);
      writeTrackingTimezoneMode(farmId, mode);
    },
    [farmId]
  );

  const formatChartTime = useCallback(
    (timeMs, variant) => formatFieldAppDateTime(timeMs, timezoneMode, variant),
    [timezoneMode]
  );

  const formatTraceTime = useCallback(
    (time) => formatFieldAppDateTime(time, timezoneMode, "map"),
    [timezoneMode]
  );

  const formatTrackingTimestamp = useCallback(
    (timeMs) => formatFieldAppDateTime(timeMs, timezoneMode, "card"),
    [timezoneMode]
  );

  const resolveGraphClickZoom = useCallback(() => {
    const stored = mapInteractionZoomRef.current;
    if (stored == null) return GRAPH_CLICK_ZOOM;
    return Math.min(MAP_ZOOM_MAX, Math.max(MAP_ZOOM_MIN, Math.round(stored)));
  }, []);

  useEffect(() => {
    const next = initialPlannedSpeed(farmId);
    setPlannedSpeedKmh(next);
    setSpeedDraft(String(next));
    setSpeedEditorOpen(false);
  }, [farmId]);

  const persistPlannedSpeed = useCallback(
    (value) => {
      const clamped = clampPlannedSpeed(value);
      setPlannedSpeedKmh(clamped);
      setSpeedDraft(String(clamped));
      writeStoredPlannedSpeed(farmId, clamped);
      setSpeedEditorOpen(false);
    },
    [farmId]
  );

  const loadTracking = useCallback(
    async ({ bustCache = false } = {}) => {
      if (!loadSelection) return;

      const generation = loadGenerationRef.current + 1;
      loadGenerationRef.current = generation;

      const cacheKey = buildTrackingCacheKey(farmId, loadSelection);
      if (bustCache) {
        clearCachedTracking(cacheKey);
      }

      const cached = !bustCache ? getCachedTracking(cacheKey) : null;
      if (cached) {
        visibleCacheKeyRef.current = cacheKey;
        unstable_batchedUpdates(() => {
          setTracking(cached);
          setLoadedFromCache(true);
          setGraphBrushRange({ startIndex: 0, endIndex: null });
          setFocusBrushRange({ token: generation });
          setSelectedActivityInterval(null);
          setSelectedTrackTimeMs(null);
          setLoadingInitial(false);
          setLoadingProgress(null);
        });
        return;
      }

      setLoadedFromCache(false);
      const samePeriod =
        visibleCacheKeyRef.current &&
        visibleCacheKeyRef.current === cacheKey;
      const keepVisibleTracking =
        samePeriod && hasDisplayableTracking(trackingRef.current);
      if (!keepVisibleTracking) {
        visibleCacheKeyRef.current = "";
        setLoadingInitial(true);
        setTracking(null);
      } else {
        setLoadingInitial(false);
      }
      setLoadingProgress(null);

      try {
        const operatorIds = loadSelection.operatorIds || [];
        const data =
          combineTrails && operatorIds.length > 1
            ? await (async () => {
                const payloads = (
                  await Promise.all(
                    operatorIds.map((operatorId) =>
                      fetchTrackingProgressive(
                        farmId,
                        buildSingleOperatorSelection(loadSelection, operatorId),
                        {
                          isCancelled: () =>
                            loadGenerationRef.current !== generation,
                        }
                      )
                    )
                  )
                ).filter(Boolean);
                const merged = mergeTrackingPayloads(payloads);
                if (merged) merged.separateTrailPayloads = payloads;
                return merged;
              })()
            : await fetchTrackingProgressive(farmId, loadSelection, {
          isCancelled: () => loadGenerationRef.current !== generation,
          onProgress: ({
            tracking: partialTracking,
            loadedDays,
            totalDays,
            complete,
          }) => {
            if (loadGenerationRef.current !== generation) return;

            setLoadingProgress({
              loadedDays,
              totalDays,
              complete: Boolean(complete),
            });

            const hasDisplayable = hasDisplayableTracking(partialTracking);
            unstable_batchedUpdates(() => {
              setTracking((current) =>
                publishedTrackingSnapshot(current, partialTracking, {
                  complete: Boolean(complete),
                  hasDisplayable,
                })
              );
              if (hasDisplayable) {
                visibleCacheKeyRef.current = cacheKey;
                setLoadingInitial(false);
              }
              if (complete) {
                setFocusBrushRange({ token: generation });
              }
            });
          },
        });

        if (loadGenerationRef.current !== generation) return;

        if (data) {
          setCachedTracking(cacheKey, data);
          if (hasDisplayableTracking(data)) {
            visibleCacheKeyRef.current = cacheKey;
          }
          unstable_batchedUpdates(() => {
            setTracking(data);
            setFocusBrushRange({ token: generation });
          });
        } else if (!hasDisplayableTracking(trackingRef.current)) {
          setTracking(null);
        }
      } catch (error) {
        if (loadGenerationRef.current !== generation) return;
        console.error(error);
        setTracking(null);
      } finally {
        if (loadGenerationRef.current === generation) {
          setLoadingInitial(false);
          setLoadingProgress(null);
        }
      }
    },
    [combineTrails, farmId, loadSelection]
  );

  const showInitialLoading =
    loadingInitial && !hasDisplayableTracking(tracking);
  const showBackgroundLoading =
    Boolean(loadingProgress && !loadingProgress.complete);

  const todayLiveTrackingEnabled = useMemo(
    () =>
      isTodayVisibleInTracking(selection, selectedDayKeys, periodDayKeys),
    [selection, selectedDayKeys, periodDayKeys]
  );

  const machineIdent = tracking && tracking.equipment && tracking.equipment.ident;

  const scheduleMachineOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    const addIdent = (ident) => {
      const value = String(ident || "").trim();
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push(value);
    };

    (selection && selection.machineIdents ? selection.machineIdents : []).forEach(addIdent);
    (loadSelection && loadSelection.machineIdents
      ? loadSelection.machineIdents
      : []
    ).forEach(addIdent);

    const equipmentIds =
      (selection && selection.equipmentIds) ||
      (loadSelection && loadSelection.equipmentIds) ||
      [];
    equipmentIds.forEach((equipmentId) => {
      const match = machines.find(
        (item) => Number(item.id) === Number(equipmentId)
      );
      if (match) addIdent(match.ident);
    });

    ((tracking && tracking.machines) || []).forEach((item) => addIdent(item.ident));
    if (machineIdent && machineIdent !== "Operador") {
      addIdent(machineIdent);
    }

    return options.sort((left, right) => left.localeCompare(right));
  }, [selection, loadSelection, machines, tracking, machineIdent]);

  useEffect(() => {
    if (
      selectedActivityInterval &&
      selectedActivityInterval.lane &&
      selectedActivityInterval.lane.type === "machine" &&
      selectedActivityInterval.lane.label
    ) {
      setScheduleMachinePick(selectedActivityInterval.lane.label);
      return;
    }
    if (!scheduleMachineOptions.length) {
      setScheduleMachinePick("");
      return;
    }
    if (
      !scheduleMachinePick ||
      scheduleMachineOptions.indexOf(scheduleMachinePick) === -1
    ) {
      setScheduleMachinePick(scheduleMachineOptions[0]);
    }
  }, [scheduleMachineOptions, scheduleMachinePick, selectedActivityInterval]);

  const scheduleMachineIdent = scheduleMachinePick || null;

  const schedulePeriodLabel = useMemo(() => {
    const source = selection || loadSelection;
    if (!source || !source.dateFrom || !source.dateTo) return "";
    const from = String(source.dateFrom).slice(0, 10);
    const to = String(source.dateTo).slice(0, 10);
    return `${from} – ${to}`;
  }, [selection, loadSelection]);

  const scheduleOperatorLabel = useMemo(() => {
    const source = selection || loadSelection;
    if (!source || !source.operatorNames || !source.operatorNames.length) {
      return "";
    }
    return source.operatorNames.join(", ");
  }, [selection, loadSelection]);

  const handleMachineScheduleSubFarmChange = useCallback(
    (subFarmId) => {
      setMachineScheduleSubFarmId(subFarmId);
      setMachineScheduleLandplotName("");
      setMachineScheduleLandplotId((currentLandplotId) => {
        if (!subFarmId || !currentLandplotId) return currentLandplotId;
        const allowedIds = landPlotIdsForSubFarm(
          landplots,
          subFarmId,
          groupFarms
        ).map(Number);
        return allowedIds.includes(Number(currentLandplotId))
          ? currentLandplotId
          : null;
      });
    },
    [groupFarms, landplots]
  );

  const handleMachineScheduleLandplotChange = useCallback((landplotId) => {
    setMachineScheduleLandplotId(landplotId);
    setMachineScheduleLandplotName("");
  }, []);

  const machineSchedulePanel =
    scheduleMachineIdent && !embedded ? (
      <div className="field-app-machine-schedule-section">
        <FieldAppMachineSchedulePanel
          expanded={machineScheduleExpanded}
          onToggleExpanded={() =>
            setMachineScheduleExpanded((value) => !value)
          }
          rows={machineScheduleRows}
          meterType={machineScheduleMeterType}
          loading={machineScheduleLoading}
          error={machineScheduleError}
          timezoneMode={timezoneMode}
          farmId={farmId}
          selection={selection || loadSelection}
          machineLabel={scheduleMachineIdent}
          machineOptions={scheduleMachineOptions}
          onMachineChange={setScheduleMachinePick}
          landplots={landplots}
          groupFarms={groupFarms}
          selectedSubFarmId={machineScheduleSubFarmId}
          onSubFarmChange={handleMachineScheduleSubFarmChange}
          selectedLandplotId={machineScheduleLandplotId}
          selectedLandplotName={machineScheduleLandplotName}
          onLandplotChange={handleMachineScheduleLandplotChange}
          periodLabel={schedulePeriodLabel}
          operatorLabel={scheduleOperatorLabel}
          t={t}
        />
      </div>
    ) : null;

  const loadMachineSchedule = useCallback(async () => {
    const source = selection || loadSelection;
    if (!scheduleMachineIdent || !source) return;
    setMachineScheduleLoading(true);
    setMachineScheduleError(null);
    try {
      const data = await fetchMachineSchedule(farmId, {
        machineIdent: scheduleMachineIdent,
        operatorIds: source.operatorIds || [],
        dateFrom: source.dateFrom,
        dateTo: source.dateTo,
        landPlotId: machineScheduleLandplotId,
      });
      setMachineScheduleRows(data.days || []);
      setMachineScheduleMeterType(data.meterType || "hour_meter");
      setMachineScheduleLandplotName(data.landPlotName || "");
    } catch (error) {
      console.error(error);
      setMachineScheduleRows([]);
      setMachineScheduleError(t("Falha ao carregar planilha da máquina"));
    } finally {
      setMachineScheduleLoading(false);
    }
  }, [farmId, loadSelection, machineScheduleLandplotId, scheduleMachineIdent, selection, t]);

  useEffect(() => {
    if (!machineScheduleLandplotId) {
      setMachineScheduleLandplotName("");
    }
  }, [machineScheduleLandplotId]);

  useEffect(() => {
    if (!machineScheduleExpanded || !scheduleMachineIdent) return;
    loadMachineSchedule();
  }, [
    machineScheduleExpanded,
    scheduleMachineIdent,
    machineScheduleLandplotId,
    loadMachineSchedule,
    refreshToken,
  ]);

  useEffect(() => {
    setMachineScheduleExpanded(false);
    setMachineScheduleRows([]);
    setMachineScheduleError(null);
    setMachineScheduleSubFarmId(null);
    setMachineScheduleLandplotId(null);
    setMachineScheduleLandplotName("");
  }, [scheduleMachineIdent]);

  useEffect(() => {
    if (
      !active ||
      !todayLiveTrackingEnabled ||
      !machineIdent ||
      loadingInitial ||
      showBackgroundLoading
    ) {
      setLiveEquipment(null);
      return undefined;
    }

    let cancelled = false;
    let timerId = null;

    const poll = async () => {
      if (cancelled || document.visibilityState !== "visible") return;

      try {
        const next = await fetchLiveEquipmentPosition(farmId, machineIdent);
        if (!cancelled && next) {
          setLiveEquipment(next);
        }
      } catch (error) {
        console.error(error);
      }
    };

    const scheduleNext = () => {
      if (cancelled) return;
      timerId = window.setTimeout(async () => {
        await poll();
        scheduleNext();
      }, LIVE_EQUIPMENT_POLL_MS);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        poll();
        if (!timerId) scheduleNext();
      } else if (timerId) {
        window.clearTimeout(timerId);
        timerId = null;
      }
    };

    poll();
    scheduleNext();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [
    active,
    farmId,
    loadingInitial,
    machineIdent,
    showBackgroundLoading,
    todayLiveTrackingEnabled,
  ]);

  useEffect(() => {
    if (!active || !loadSelection) return;

    const bustCache = lastRefreshTokenRef.current !== refreshToken;
    lastRefreshTokenRef.current = refreshToken;
    loadTracking({ bustCache });
  }, [active, loadSelection, loadTracking, refreshToken]);

  useEffect(() => {
    if (!active || !farmId) return;
    Promise.all([
      axios.get(`${myConfig.API_URL}/landplot/?farm_id=${farmId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      }),
      fetchGroupFarms(farmId).catch(() => []),
    ])
      .then(([landRes, farms]) => {
        if (landRes.status === 200) {
          setLandplots(landRes.data || []);
        } else {
          setLandplots([]);
        }
        setGroupFarms(Array.isArray(farms) ? farms : []);
      })
      .catch(() => {
        setLandplots([]);
        setGroupFarms([]);
      });
  }, [active, farmId]);

  const rawPathPoints = useMemo(() => {
    if (!tracking || !tracking.map) return [];
    return extractRawPathPoints(tracking.map.lines || []);
  }, [tracking]);

  const fullDisplayLines = useMemo(
    () => rebuildTrackingLines(rawPathPoints, plannedSpeedKmh),
    [rawPathPoints, plannedSpeedKmh]
  );

  const fullGraphData = useMemo(() => buildGraphSeries({ lines: fullDisplayLines }), [fullDisplayLines]);

  const graphDayRanges = useMemo(
    () => buildGraphDayRanges(fullGraphData),
    [fullGraphData]
  );

  const calendarDayRanges = useMemo(
    () =>
      buildCalendarDayRanges(
        selection && selection.dateFrom,
        selection && selection.dateTo,
        graphDayRanges
      ),
    [selection, graphDayRanges]
  );

  const daysWithTracking = useMemo(
    () => getDaysWithTracking(calendarDayRanges),
    [calendarDayRanges]
  );

  const chartPeriodDayKeys = useMemo(
    () =>
      periodDayKeys.length
        ? periodDayKeys
        : daysWithTracking.map((day) => day.key),
    [periodDayKeys, daysWithTracking]
  );

  const allTrackDaysSelected = useMemo(
    () => allPeriodDaysSelected(chartPeriodDayKeys, selectedDayKeys),
    [chartPeriodDayKeys, selectedDayKeys]
  );

  const handleToggleDay = useCallback(
    (dayKey) => {
      if (!onSelectedDayKeysChange) return;
      onSelectedDayKeysChange(
        applyChartDayClick(selectedDayKeys, dayKey, chartPeriodDayKeys)
      );
    },
    [chartPeriodDayKeys, onSelectedDayKeysChange, selectedDayKeys]
  );

  const handleSelectAllDays = useCallback(() => {
    if (!onSelectedDayKeysChange) return;
    onSelectedDayKeysChange(chartPeriodDayKeys.slice());
  }, [chartPeriodDayKeys, onSelectedDayKeysChange]);

  const filteredPathPoints = useMemo(
    () =>
      filterPathPointsByTimeRange(
        rawPathPoints,
        queryTimeBounds.startMs,
        queryTimeBounds.endMs
      ),
    [rawPathPoints, queryTimeBounds.startMs, queryTimeBounds.endMs]
  );

  const chartDisplayLines = useMemo(
    () => rebuildTrackingLines(filteredPathPoints, plannedSpeedKmh),
    [filteredPathPoints, plannedSpeedKmh]
  );

  const chartSeriesSource = useMemo(
    () => ({ lines: chartDisplayLines }),
    [chartDisplayLines]
  );

  const flatLineArray = useMemo(
    () => buildFlatLineArray(chartSeriesSource),
    [chartSeriesSource]
  );
  const graphData = useMemo(
    () => buildGraphSeries(chartSeriesSource),
    [chartSeriesSource]
  );

  const chartGraphData = useMemo(
    () => (daySelectionReady && graphData.length ? graphData : []),
    [graphData, daySelectionReady]
  );

  const selectedLaneType =
    selectedActivityInterval &&
    selectedActivityInterval.lane &&
    selectedActivityInterval.lane.type;
  const useSelectedDaysOnMap = Boolean(selectedLaneType) || mapFullDayPath;

  const mapRangeStartMs = useSelectedDaysOnMap
    ? queryTimeBounds.startMs
    : visibleGraphRange.startTimeMs;
  const mapRangeEndMs = useSelectedDaysOnMap
    ? queryTimeBounds.endMs
    : visibleGraphRange.endTimeMs;

  const mapPathPoints = useMemo(() => {
    if (selectedLaneType === "telemetry" && tracking && tracking.operatorGraphLines) {
      const operatorPoints = extractRawPathPoints(tracking.operatorGraphLines);
      if (operatorPoints.length) {
        return filterPathPointsByTimeRange(
          operatorPoints,
          mapRangeStartMs,
          mapRangeEndMs
        );
      }
    }
    return filterPathPointsByTimeRange(
      filteredPathPoints,
      mapRangeStartMs,
      mapRangeEndMs
    );
  }, [
    filteredPathPoints,
    mapRangeEndMs,
    mapRangeStartMs,
    selectedLaneType,
    tracking,
  ]);

  const mapDisplayLines = useMemo(() => {
    if (
      combineTrails &&
      tracking &&
      tracking.separateTrailPayloads &&
      tracking.separateTrailPayloads.length
    ) {
      return combineOperatorTrailLines(
        tracking.separateTrailPayloads,
        plannedSpeedKmh,
        mapRangeStartMs,
        mapRangeEndMs
      );
    }
    return rebuildTrackingLines(mapPathPoints, plannedSpeedKmh);
  }, [
    combineTrails,
    mapPathPoints,
    mapRangeEndMs,
    mapRangeStartMs,
    plannedSpeedKmh,
    tracking,
  ]);

  const operatorMarkerPosition = useMemo(() => {
    const chosen = lastValidPointFromLines(mapDisplayLines);
    if (!chosen) return null;
    return {
      lat: chosen.lat,
      lng: chosen.lng,
      speed: chosen.speed,
      lastMovedAt: chosen.time || chosen.iso || chosen.date,
    };
  }, [mapDisplayLines]);

  const mapState = useMemo(() => {
    if (!tracking || !tracking.map) return null;
    return {
      centroidLat: tracking.map.centroidLat,
      centroidLng: tracking.map.centroidLng,
      geojson: tracking.map.geojson || "",
      lines: mapDisplayLines,
      operation_type: tracking.map.operation_type || "field_app",
      stopped_points: stoppedPointsFromLines(mapDisplayLines),
    };
  }, [tracking, mapDisplayLines]);

  useEffect(() => {
    if (
      !chartGraphData.length ||
      !daySelectionReady ||
      !selectedDayKeys.length ||
      queryTimeBounds.startMs == null ||
      queryTimeBounds.endMs == null
    ) {
      return;
    }

    const focusKey = [
      selectedDayKeys.join(","),
      queryTimeBounds.startMs,
      queryTimeBounds.endMs,
    ].join("|");

    if (chartFocusKeyRef.current === focusKey) {
      return;
    }
    chartFocusKeyRef.current = focusKey;

    const { startIndex, endIndex } = graphIndicesForTimeRange(
      chartGraphData,
      queryTimeBounds.startMs,
      queryTimeBounds.endMs
    );

    setGraphBrushRange({ startIndex, endIndex });
    setVisibleGraphRange({
      startTimeMs: queryTimeBounds.startMs,
      endTimeMs: queryTimeBounds.endMs,
    });
    setFocusBrushRange({ startIndex, endIndex });
  }, [
    selectedDayKeys,
    chartGraphData.length,
    daySelectionReady,
    queryTimeBounds.startMs,
    queryTimeBounds.endMs,
  ]);

  const visiblePathPoints = useMemo(
    () => sliceVisiblePathPoints(flatLineArray, graphBrushRange, chartGraphData),
    [flatLineArray, graphBrushRange, chartGraphData]
  );

  const periodStats = useMemo(() => {
    if (!chartGraphData.length) return null;
    const visible =
      graphBrushRange.endIndex == null
        ? chartGraphData
        : chartGraphData.slice(
            graphBrushRange.startIndex,
            graphBrushRange.endIndex + 1
          );
    return calculatePeriodMotionStats(visible);
  }, [graphBrushRange, chartGraphData]);

  const periodPathStats = useMemo(
    () => calculatePathDistanceStats(visiblePathPoints),
    [visiblePathPoints]
  );

  const totalPathStats = useMemo(
    () => calculatePathDistanceStats(flatLineArray),
    [flatLineArray]
  );

  const totalAreaHa = useMemo(
    () => estimateWorkedAreaHa(totalPathStats.distanceKm, DEFAULT_WORKING_WIDTH_M),
    [totalPathStats.distanceKm]
  );

  const periodAreaHa = useMemo(
    () => estimateWorkedAreaHa(periodPathStats.distanceKm, DEFAULT_WORKING_WIDTH_M),
    [periodPathStats.distanceKm]
  );

  useEffect(() => {
    if (!active || !mapState || !window.google || !window.google.map) return;
    const timer = window.setTimeout(() => {
      window.google.maps.event.trigger(window.google.map, "resize");
    }, 250);
    return () => window.clearTimeout(timer);
  }, [active, mapState, plannedSpeedKmh, mapHeightPx]);

  useEffect(() => {
    if (!active || !tracking) return undefined;

    let cancelled = false;
    let listener = null;
    let retryTimer = null;
    let attempts = 0;

    const attachZoomListener = () => {
      if (cancelled) return;
      const google = window.google;
      if (!google || !google.map) {
        if (attempts < 24) {
          attempts += 1;
          retryTimer = window.setTimeout(attachZoomListener, 250);
        }
        return;
      }

      listener = google.maps.event.addListener(google.map, "zoom_changed", () => {
        const zoom = google.map.getZoom();
        if (Number.isFinite(zoom)) {
          mapInteractionZoomRef.current = zoom;
        }
      });
    };

    attachZoomListener();

    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      if (listener && window.google && window.google.maps) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [active, tracking]);

  const beginMapResize = useCallback((event) => {
    event.preventDefault();
    const clientY =
      event.touches && event.touches.length
        ? event.touches[0].clientY
        : event.clientY;
    mapResizeRef.current = {
      active: true,
      startY: clientY,
      startHeight: mapHeightRef.current,
    };
    document.body.classList.add("field-app-map-resizing");
  }, []);

  useEffect(() => {
    const clampMapHeight = (value) =>
      Math.min(MAP_HEIGHT_MAX, Math.max(MAP_HEIGHT_MIN, value));

    const onMove = (event) => {
      if (!mapResizeRef.current.active) return;
      if (event.cancelable) event.preventDefault();
      const clientY =
        event.touches && event.touches.length
          ? event.touches[0].clientY
          : event.clientY;
      const delta = clientY - mapResizeRef.current.startY;
      setMapHeightPx(
        clampMapHeight(mapResizeRef.current.startHeight + delta)
      );
    };

    const finishResize = () => {
      if (!mapResizeRef.current.active) return;
      mapResizeRef.current.active = false;
      document.body.classList.remove("field-app-map-resizing");
      try {
        localStorage.setItem(
          MAP_HEIGHT_STORAGE_KEY,
          String(mapHeightRef.current)
        );
      } catch (error) {
        /* ignore */
      }
      if (window.google && window.google.map) {
        window.google.maps.event.trigger(window.google.map, "resize");
      }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", finishResize);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", finishResize);
    window.addEventListener("touchcancel", finishResize);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", finishResize);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", finishResize);
      window.removeEventListener("touchcancel", finishResize);
      document.body.classList.remove("field-app-map-resizing");
    };
  }, []);

  const applyPlannedSpeed = useCallback(() => {
    const value = Number(speedDraft);
    if (!value || value <= 0 || value > PLANNED_SPEED_MAX) return;
    persistPlannedSpeed(value);
  }, [speedDraft, persistPlannedSpeed]);

  const handleUseAverageSpeed = useCallback(() => {
    const avg = tracking && tracking.summary && tracking.summary.avgSpeedKmh;
    if (!avg) return;
    persistPlannedSpeed(Math.round(avg * 10) / 10);
  }, [tracking, persistPlannedSpeed]);

  const handleUseMaxSpeed = useCallback(() => {
    const max = tracking && tracking.summary && tracking.summary.maxSpeedKmh;
    if (!max) return;
    persistPlannedSpeed(Math.round(max * 10) / 10);
  }, [tracking, persistPlannedSpeed]);

  const summaryCards = useMemo(() => {
    if (!tracking) return [];
    const operatorNames = (tracking.operators || [])
      .map((item) => item.name)
      .join(", ");
    const assumedMachineLabels = machinesActiveInGraph(
      tracking,
      chartGraphData
    ).map((item) => item.ident);
    const movingHours = periodStats
      ? Math.round((periodStats.inMotionTimeMs / 3600000) * 100) / 100
      : 0;
    return [
      {
        label: t("Operadores"),
        value: operatorNames || "—",
        hint: t("{{count}} pontos GPS do tablet", {
          count: periodPathStats.pointCount || 0,
        }),
      },
      {
        label: t("Máquinas"),
        value: assumedMachineLabels.join(", ") || "—",
        hint: t("{{count}} máquina(s) no dia visível", {
          count: assumedMachineLabels.length,
        }),
      },
      {
        label: t("Distância percorrida"),
        value: `${periodPathStats.distanceKm || 0} km`,
        hint: t("Vel. média {{speed}} km/h", {
          speed: periodPathStats.avgSpeedKmh || 0,
        }),
      },
      {
        label: t("Área estimada"),
        value: `${periodAreaHa} ha`,
        hint: t("Estimativa com largura de {{width}} m", {
          width: DEFAULT_WORKING_WIDTH_M,
        }),
      },
      {
        label: t("Tempo em movimento"),
        value: `${movingHours} h`,
        hint: t("Vel. máx. {{speed}} km/h", {
          speed: periodPathStats.maxSpeedKmh || 0,
        }),
      },
    ];
  }, [
    chartGraphData,
    periodAreaHa,
    periodPathStats,
    periodStats,
    t,
    tracking,
  ]);

  const handleGraphRangeChange = useCallback((range) => {
    setGraphBrushRange((current) => {
      if (
        current &&
        current.startIndex === range.startIndex &&
        current.endIndex === range.endIndex
      ) {
        return current;
      }
      return range;
    });
    setVisibleGraphRange((current) => {
      if (
        current.startTimeMs === range.startTimeMs &&
        current.endTimeMs === range.endTimeMs
      ) {
        return current;
      }
      return {
        startTimeMs: range.startTimeMs,
        endTimeMs: range.endTimeMs,
      };
    });
  }, []);

  const handleFocusGraphPeriod = useCallback(
    (startIso, endIso, options = {}) => {
      const focus = focusGraphIndicesForPeriod(chartGraphData, startIso, endIso);
      if (!focus) {
        if (!options.silent) {
          toast.error(
            t("Não há pontos GPS no gráfico para o período desta ordem.")
          );
        }
        return null;
      }
      setGraphBrushRange({
        startIndex: focus.startIndex,
        endIndex: focus.endIndex,
      });
      setVisibleGraphRange({
        startTimeMs: focus.startTimeMs,
        endTimeMs: focus.endTimeMs,
      });
      setFocusBrushRange({
        startIndex: focus.startIndex,
        endIndex: focus.endIndex,
        token: Date.now(),
      });
      return focus;
    },
    [chartGraphData, t]
  );

  const focusMapOnTimeRange = useCallback((startMs, endMs, sourcePoints) => {
    const google = window.google;
    if (!google || !google.map || startMs == null || endMs == null) {
      return false;
    }
    const pool =
      sourcePoints && sourcePoints.length ? sourcePoints : flatLineArray;
    const points = pool.filter((point) => {
      const ms = new Date(point.time).getTime();
      return !Number.isNaN(ms) && ms >= startMs && ms <= endMs;
    });
    const valid = points.filter(
      (point) =>
        point.lat != null &&
        point.lng != null &&
        Number.isFinite(Number(point.lat)) &&
        Number.isFinite(Number(point.lng))
    );
    if (!valid.length) {
      return false;
    }
    if (valid.length === 1 || !google.maps || !google.maps.LatLngBounds) {
      const anchor = valid[0];
      google.map.setCenter({ lat: Number(anchor.lat), lng: Number(anchor.lng) });
      google.map.setZoom(TELEMETRY_POPUP_MAP_ZOOM);
      mapInteractionZoomRef.current = TELEMETRY_POPUP_MAP_ZOOM;
      return true;
    }
    const bounds = new google.maps.LatLngBounds();
    valid.forEach((point) => {
      bounds.extend({ lat: Number(point.lat), lng: Number(point.lng) });
    });
    google.map.fitBounds(bounds, 48);
    const fittedZoom = google.map.getZoom();
    if (fittedZoom != null && fittedZoom > TELEMETRY_POPUP_MAP_ZOOM) {
      google.map.setZoom(TELEMETRY_POPUP_MAP_ZOOM);
    }
    mapInteractionZoomRef.current = google.map.getZoom();
    return true;
  }, [flatLineArray]);

  const embeddedSampleMapZoom = highlightMapPoint
    ? SAMPLE_POINT_MAP_ZOOM
    : TELEMETRY_POPUP_MAP_ZOOM;

  const focusEmbeddedSamplePoint = useCallback(() => {
    if (!embedded || !initialMapCenter) return false;
    const lat = Number(initialMapCenter.lat);
    const lng = Number(initialMapCenter.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    const google = window.google;
    if (!google || !google.map) return false;
    google.map.setCenter({ lat, lng });
    google.map.setZoom(embeddedSampleMapZoom);
    mapInteractionZoomRef.current = embeddedSampleMapZoom;
    return true;
  }, [embedded, embeddedSampleMapZoom, initialMapCenter]);

  const activityLanes = useMemo(
    () =>
      buildDisplayActivityLanes(
        tracking
          ? {
              ...tracking,
              telemetryGraphData: buildGraphSeriesFromLines(
                tracking.operatorGraphLines || []
              ),
            }
          : null,
        chartGraphData
      ),
    [tracking, chartGraphData]
  );

  const telemetryMarkers = useMemo(
    () =>
      telemetryBoundaryMarkers(tracking && tracking.activityLanes).map(
        (marker) => ({
          ...marker,
          label:
            marker.kind === "start"
              ? t("Início da telemetria")
              : t("Fim da telemetria"),
        })
      ),
    [tracking, t]
  );

  const machinesInChartPeriod = useMemo(
    () => machinesActiveInGraph(tracking, chartGraphData),
    [tracking, chartGraphData]
  );
  const hasActivityData = useMemo(() => {
    return activityLanes.some(
      (lane) => lane.intervals && lane.intervals.length > 0
    );
  }, [activityLanes]);

  useEffect(() => {
    if (!focusSessionUuid || !activityLanes.length) return;

    for (const lane of activityLanes) {
      for (const interval of lane.intervals || []) {
        const sessionUuid =
          interval.sessionUuid ||
          (interval.sessionDetail && interval.sessionDetail.sessionUuid);
        if (sessionUuid !== focusSessionUuid) continue;

        const startMs = new Date(interval.start).getTime();
        const endMs = new Date(interval.end).getTime();
        if (Number.isNaN(startMs) || Number.isNaN(endMs)) continue;

        setClosureChartPickActive(false);
        setClosureChartPoint(null);
        setSelectedActivityInterval({
          lane,
          interval,
          startMs,
          endMs,
        });
        if (onFocusSessionHandled) {
          onFocusSessionHandled();
        }
        return;
      }
    }
  }, [focusSessionUuid, activityLanes, onFocusSessionHandled]);

  useEffect(() => {
    if (!focusGraphPeriod || !focusGraphPeriod.startIso || !focusGraphPeriod.endIso) {
      return;
    }
    if (!chartGraphData.length) {
      return;
    }
    const focus = handleFocusGraphPeriod(
      focusGraphPeriod.startIso,
      focusGraphPeriod.endIso,
      { silent: Boolean(focusGraphPeriod.silent) }
    );
    if (!focus) {
      return;
    }
    if (embedded && !mapFullDayPath) {
      mapInteractionZoomRef.current = TELEMETRY_POPUP_MAP_ZOOM;
      window.setTimeout(() => {
        const lat = Number(initialMapCenter && initialMapCenter.lat);
        const lng = Number(initialMapCenter && initialMapCenter.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          const google = window.google;
          if (google && google.map) {
            google.map.setCenter({ lat, lng });
            google.map.setZoom(TELEMETRY_POPUP_MAP_ZOOM);
            return;
          }
        }
        focusMapOnTimeRange(focus.startTimeMs, focus.endTimeMs);
      }, 350);
    }
    if (onFocusGraphPeriodHandled) {
      onFocusGraphPeriodHandled();
    }
  }, [
    focusGraphPeriod,
    chartGraphData,
    embedded,
    mapFullDayPath,
    handleFocusGraphPeriod,
    focusMapOnTimeRange,
    onFocusGraphPeriodHandled,
    initialMapCenter,
  ]);

  useEffect(() => {
    if (!embedded || !initialMapCenter) return undefined;

    const timer = window.setTimeout(() => {
      focusEmbeddedSamplePoint();
    }, 500);

    return () => window.clearTimeout(timer);
  }, [
    embedded,
    initialMapCenter,
    refreshToken,
    mapPathPoints.length,
    focusEmbeddedSamplePoint,
  ]);

  const fuelRecords = useMemo(() => {
    const summary = tracking && tracking.fuelSummary;
    return (summary && summary.records) || [];
  }, [tracking]);

  const periodFuelStats = useMemo(() => {
    const startMs =
      visibleGraphRange.startTimeMs != null
        ? visibleGraphRange.startTimeMs
        : periodStats && periodStats.startTimeMs;
    const endMs =
      visibleGraphRange.endTimeMs != null
        ? visibleGraphRange.endTimeMs
        : periodStats && periodStats.endTimeMs;
    return fuelStatsForRange(fuelRecords, startMs, endMs);
  }, [fuelRecords, periodStats, visibleGraphRange]);

  const highlightedWeatherPoint =
    highlightMapPoint ||
    closureChartPoint ||
    (mapPathPoints.length ? mapPathPoints[mapPathPoints.length - 1] : null);
  const weatherSeries = pickWeatherSeries(tracking, highlightedWeatherPoint);
  const weatherFallbackSeries = pickWeatherFallbackSeries(
    tracking,
    highlightedWeatherPoint
  );

  const weatherHtmlForPoint = useCallback(
    (point) =>
      loadWeatherInfoWindowHtml({
        farmId,
        point,
        operatorIds: (displaySelection && displaySelection.operatorIds) || [],
        t,
      }),
    [displaySelection, farmId, t]
  );

  const handlePathPointSelected = useCallback((point) => {
    if (graphMarkerRef.current) {
      graphMarkerRef.current.setMap(null);
      graphMarkerRef.current = null;
    }
    const nextMs = weatherTimeMs(point && (point.time || point.timestamp));
    if (nextMs != null) {
      setSelectedTrackTimeMs(nextMs);
    }
  }, []);

  const handleActivityIntervalClick = useCallback(
    (detail) => {
      setClosureChartPickActive(false);
      setClosureChartPoint(null);
      setSelectedActivityInterval(detail);

      const startMs = queryTimeBounds.startMs;
      const endMs = queryTimeBounds.endMs;
      if (startMs == null || endMs == null) return;

      handleFocusGraphPeriod(
        new Date(startMs).toISOString(),
        new Date(endMs).toISOString(),
        { silent: true }
      );

      const trackingNow = trackingRef.current;
      const focusPoints =
        detail &&
        detail.lane &&
        detail.lane.type === "telemetry" &&
        trackingNow &&
        trackingNow.operatorGraphLines
          ? extractRawPathPoints(trackingNow.operatorGraphLines)
          : null;

      window.setTimeout(() => {
        if (mapSectionRef.current && mapSectionRef.current.scrollIntoView) {
          mapSectionRef.current.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
        focusMapOnTimeRange(startMs, endMs, focusPoints);
      }, 350);
    },
    [
      focusMapOnTimeRange,
      handleFocusGraphPeriod,
      queryTimeBounds.endMs,
      queryTimeBounds.startMs,
    ]
  );

  const handleStartClosureChartPick = useCallback(() => {
    setClosureChartPickActive(true);
    setClosureChartPoint(null);
  }, []);

  const handleStopClosureChartPick = useCallback(() => {
    setClosureChartPickActive(false);
  }, []);

  const handleClearClosureChartPoint = useCallback(() => {
    setClosureChartPoint(null);
    if (graphMarkerRef.current) {
      graphMarkerRef.current.setMap(null);
      graphMarkerRef.current = null;
    }
  }, []);

  const handleGraphClick = useCallback(
    (point) => {
      if (!point) return;

      if (closureChartPickActive) {
        const coords =
          flatLineArray.find(({ time }) => time === point.timestamp) ||
          flatLineArray.find(
            ({ time }) => new Date(time).getTime() === point.time
          );

        if (!coords) return;

        setClosureChartPoint({
          time: coords.time,
          lat: coords.lat,
          lng: coords.lng,
          speed: coords.speed || null,
        });

        if (window.google && window.google.map) {
          if (graphMarkerRef.current) {
            graphMarkerRef.current.setMap(null);
          }
          const google = window.google;
          graphMarkerRef.current = new google.maps.Marker({
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: "#E67E22",
              fillOpacity: 1,
              strokeColor: "#FFFFFF",
              strokeWeight: 2,
            },
            position: { lat: coords.lat, lng: coords.lng },
            map: google.map,
          });
          google.map.setCenter({ lat: coords.lat, lng: coords.lng });
          const zoom = resolveGraphClickZoom();
          google.map.setZoom(zoom);
          mapInteractionZoomRef.current = zoom;
        }
        return;
      }

      if (!window.google || !window.google.map) return;

      setDirectionMarkerClearToken((value) => value + 1);

      const coords =
        flatLineArray.find(({ time }) => time === point.timestamp) ||
        flatLineArray.find(
          ({ time }) => new Date(time).getTime() === point.time
        );

      if (!coords) return;

      setSelectedTrackTimeMs(weatherTimeMs(coords.time));

      if (graphMarkerRef.current) {
        graphMarkerRef.current.setMap(null);
      }

      const google = window.google;
      graphMarkerRef.current = new google.maps.Marker({
        icon: {
          path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          rotation: coords.course || 0,
          strokeColor: coords.color || "#007BFF",
          fillColor: coords.color || "#007BFF",
          scale: 3,
          strokeWeight: 3.5,
          fillOpacity: 0.9,
        },
        position: { lat: coords.lat, lng: coords.lng },
        map: google.map,
      });

      google.map.setCenter({ lat: coords.lat, lng: coords.lng });
      const zoom = resolveGraphClickZoom();
      google.map.setZoom(zoom);
      mapInteractionZoomRef.current = zoom;

      const buildGraphPointContent = (weatherHtml) =>
        pointDetailsInfoWindowHtml({
          t,
          weatherHtml,
          speed: coords.speed || "—",
          timeLabel: formatTraceTime(coords.time),
          relativeTime: formatRelativeTime(coords.time, t),
        });
      const infoWindow = new google.maps.InfoWindow({
        content: buildGraphPointContent(weatherLoadingHtml(t)),
        maxWidth: 260,
      });
      infoWindow.open(google.map, graphMarkerRef.current);
      weatherHtmlForPoint(coords)
        .then((weatherHtml) => {
          infoWindow.setContent(buildGraphPointContent(weatherHtml || ""));
        })
        .catch(() => {
          infoWindow.setContent(buildGraphPointContent(""));
        });
      window.setTimeout(() => infoWindow.close(), POINT_DETAILS_INFO_WINDOW_MS);
    },
    [
      closureChartPickActive,
      flatLineArray,
      formatTraceTime,
      resolveGraphClickZoom,
      t,
      weatherHtmlForPoint,
    ]
  );

  const handleTelemetryMarkerClick = useCallback(
    (marker) => {
      if (!marker || marker.timeMs == null || !flatLineArray.length) return;

      let nearest = null;
      let nearestDelta = Infinity;
      flatLineArray.forEach((point) => {
        const pointMs = new Date(point.time).getTime();
        if (Number.isNaN(pointMs)) return;
        const delta = Math.abs(pointMs - marker.timeMs);
        if (delta < nearestDelta) {
          nearest = point;
          nearestDelta = delta;
        }
      });
      if (!nearest) return;

      if (mapSectionRef.current && mapSectionRef.current.scrollIntoView) {
        mapSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }

      handleGraphClick({
        timestamp: nearest.time,
        time: new Date(nearest.time).getTime(),
      });
    },
    [flatLineArray, handleGraphClick]
  );

  const mapReady = Boolean(tracking && mapState);

  if (!mapReady && showInitialLoading) {
    return (
      <div className="field-app-tracking-loading-panel">
        <MDSpinner singleColor="#1ABB9C" size={48} />
        <FaSatelliteDish size={28} color="#1ABB9C" aria-hidden />
        <p className="field-app-tracking-loading-title">
          {t("Carregando telemetria...")}
        </p>
        <p className="field-app-tracking-loading-subtitle">
          {loadingProgress && loadingProgress.totalDays > 1
            ? t("Abrindo o mapa do dia mais recente.")
            : t("Preparando mapa e gráfico de rastreamento.")}
        </p>
      </div>
    );
  }

  if (!mapReady) {
    const machineOnly = Boolean(selection && selection.machineTrackOnly);
    return (
      <div className="field-app-tracking-panel">
        <div className="field-app-tracking-empty">
          <p>
            {machineOnly
              ? t(
                  "Nenhum ponto GPS do rastreador desta máquina no período selecionado."
                )
              : t(
                  "Nenhum rastreamento encontrado para os operadores, máquinas e período selecionados."
                )}
          </p>
          <p className="text-muted">
            {machineOnly
              ? t(
                  "Confirme se o rastreador estava ativo e se o intervalo de datas inclui os dias em que a máquina se deslocou. Ampliar o período pode trazer rotas gravadas em outras datas."
                )
              : t(
                  "Confirme se o operador usou o app com GPS ativo ou se a máquina enviou posições no intervalo escolhido."
                )}
          </p>
        </div>
        {machineSchedulePanel}
      </div>
    );
  }

  const equipment = tracking.equipment || null;
  const mapEquipment = resolveOperatorMapEquipment(
    {
      ...(equipment || {}),
      speed:
        equipment && equipment.speed != null
          ? equipment.speed
          : operatorMarkerPosition && operatorMarkerPosition.speed,
      lastMovedAt:
        (equipment && equipment.lastMovedAt) ||
        (operatorMarkerPosition && operatorMarkerPosition.lastMovedAt),
    },
    liveEquipment
  );
  const summary = tracking.summary || {};

  const periodCardData = periodStats
    ? [
        {
          title: t("Operador"),
          value:
            (tracking.operators || []).map((item) => item.name).join(", ") ||
            "—",
        },
        {
          title: t("Máquina"),
          value:
            machinesInChartPeriod.map((item) => item.ident).join(", ") || "—",
        },
        {
          title: t("Hora de início"),
          value: formatTrackingTimestamp(periodStats.startTimeMs),
        },
        {
          title: t("Hora de fim"),
          value: formatTrackingTimestamp(periodStats.endTimeMs),
        },
        {
          title: t("Tempo em movimento"),
          value: formatPeriodClockDuration(periodStats.inMotionTimeMs),
        },
        {
          title: t("Tempo parado / sem sinal"),
          value: formatPeriodClockDuration(periodStats.idleTimeMs),
        },
        {
          title: t("Distância do período"),
          value: `${periodPathStats.distanceKm} km`,
        },
        {
          title: t("Área estimada do período"),
          value: `${periodAreaHa} ha`,
        },
        {
          title: t("Vel. média / máx."),
          value: `${periodPathStats.avgSpeedKmh} / ${periodPathStats.maxSpeedKmh} km/h`,
        },
        {
          title: t("Pontos GPS no trecho"),
          value: String(periodPathStats.pointCount),
        },
        {
          title: t("Combustível no período"),
          value:
            periodFuelStats.recordCount > 0
              ? `${periodFuelStats.totalLiters} L`
              : "—",
        },
        {
          title: t("Média por abastecimento"),
          value:
            periodFuelStats.recordCount > 0
              ? `${periodFuelStats.avgLitersPerStop} L`
              : "—",
        },
        ...(periodFuelStats.byMachine.length > 1
          ? periodFuelStats.byMachine.map((machine) => ({
              title: t("Combustível — {{machine}}", { machine: machine.ident }),
              value: `${machine.liters} L (${machine.count} abast.)`,
            }))
          : []),
      ]
    : [];

  const totalCardData = [
    {
      title: t("Primeiro ponto"),
      value: tracking.positionRange.init
        ? formatTrackingTimestamp(tracking.positionRange.init)
        : "—",
    },
    {
      title: t("Último ponto"),
      value: tracking.positionRange.end
        ? formatTrackingTimestamp(tracking.positionRange.end)
        : "—",
    },
    {
      title: t("Distância total"),
      value: `${summary.distanceKm || totalPathStats.distanceKm || 0} km`,
    },
    {
      title: t("Área estimada total"),
      value: `${totalAreaHa} ha`,
    },
    {
      title: t("Tempo em movimento"),
      value: `${summary.movingTimeHours || 0} h`,
    },
    {
      title: t("Vel. média / máx."),
      value: `${summary.avgSpeedKmh || totalPathStats.avgSpeedKmh || 0} / ${
        summary.maxSpeedKmh || totalPathStats.maxSpeedKmh || 0
      } km/h`,
    },
    {
      title: t("Total de pontos"),
      value: String(summary.pointCount || totalPathStats.pointCount || 0),
    },
    {
      title: t("Combustível total"),
      value:
        tracking.fuelSummary && tracking.fuelSummary.totalLiters
          ? `${tracking.fuelSummary.totalLiters} L`
          : "—",
    },
    {
      title: t("Abastecimentos"),
      value: String(
        (tracking.fuelSummary && tracking.fuelSummary.recordCount) || 0
      ),
    },
    {
      title: t("Média por abastecimento (total)"),
      value:
        tracking.fuelSummary && tracking.fuelSummary.recordCount
          ? `${tracking.fuelSummary.avgLitersPerStop} L`
          : "—",
    },
    {
      title: t("Referência de velocidade"),
      value: `${plannedSpeedKmh} km/h`,
    },
    {
      title: t("Pontos do tablet"),
      value: String(tracking.operatorPointCount || 0),
    },
    {
      title: t("Pontos do rastreador"),
      value: String(tracking.machinePointCount || 0),
    },
  ];

  return (
    <div
      className={
        embedded
          ? "field-app-tracking field-app-tracking--embedded"
          : "field-app-tracking"
      }
    >
      {showBackgroundLoading ? (
        <div className="field-app-tracking-progress" role="status" aria-live="polite">
          <MDSpinner singleColor="#1ABB9C" size={22} />
          <div className="field-app-tracking-progress-copy">
            <strong>{t("Carregando telemetria...")}</strong>
            <span>
              {t("Preparando o período em segundo plano ({{current}}/{{total}}).", {
                current: loadingProgress.loadedDays,
                total: loadingProgress.totalDays,
              })}
            </span>
          </div>
          <div
            className="field-app-tracking-progress-bar"
            aria-hidden
            style={{
              width: `${Math.round(
                (loadingProgress.loadedDays / loadingProgress.totalDays) * 100
              )}%`,
            }}
          />
        </div>
      ) : null}

      {!embedded ? (
      <p className="field-app-intro field-app-intro--compact">
        {t(
          combineTrails
            ? "Mapa com o rastro de todas as máquinas que fizeram este trabalho, juntas na mesma tela."
            : "Rastreamento do operador selecionado no período — mapa e gráfico com telemetria do tablet e GPS das máquinas que ele assumiu. Apenas um operador por vez."
        )}
        {loadedFromCache && !showBackgroundLoading ? (
          <>
            {" "}
            {t(
              "Período carregado localmente — zoom e dias no gráfico não consultam o servidor."
            )}
          </>
        ) : null}
      </p>
      ) : null}

      {!embedded ? (
      <>
      {!combineTrails ? (
      <div className="field-app-summary-grid">
        {summaryCards.map((card) => (
          <div className="field-app-summary-card" key={card.label}>
            <div className="field-app-summary-value field-app-summary-value--text">
              {card.value}
            </div>
            <div className="field-app-summary-label">{card.label}</div>
            <div className="field-app-summary-hint">{card.hint}</div>
          </div>
        ))}
      </div>
      ) : null}

      {calendarDayRanges.length ? (
        <div className="field-app-graph-day-filters field-app-graph-day-filters--above-map">
          <div className="field-app-graph-day-filters-toolbar">
            <div className="field-app-graph-day-filters-toolbar-start">
              <span className="field-app-graph-day-filters-label">
                {t(combineTrails ? "Dias no mapa" : "Dias no gráfico")}:
              </span>
              <button
                type="button"
                className={`btn btn-xs field-app-graph-day-btn field-app-graph-day-btn--all${
                  allTrackDaysSelected ? " is-selected" : ""
                }`}
                onClick={handleSelectAllDays}
                disabled={!chartPeriodDayKeys.length}
              >
                {t("Semana inteira")}
              </button>
            </div>
            <div className="field-app-graph-day-filters-toolbar-center">
              <FieldAppTimezoneToggle
                mode={timezoneMode}
                onModeChange={handleTimezoneModeChange}
                t={t}
              />
            </div>
            <span className="field-app-graph-day-filters-hint field-app-graph-day-filters-hint--compact field-app-graph-day-filters-toolbar-end">
              {t(
                "Verde = com GPS · Cinza = sem rastreamento · Semana e mês já abrem todos os dias; o mapa carrega um dia de cada vez"
              )}
            </span>
          </div>
          <div className="field-app-graph-day-filters-days">
            {calendarDayRanges.map((dayRange) => {
              const isSelected = selectedDayKeys.indexOf(dayRange.key) !== -1;
              const hasData = Boolean(dayRange.hasData);
              return (
                <button
                  key={dayRange.key}
                  type="button"
                  className={`btn btn-xs field-app-graph-day-btn${
                    isSelected ? " is-selected" : ""
                  }${!hasData ? " is-empty" : ""}`}
                  title={
                    hasData
                      ? dayRange.label
                      : t("{{date}} — sem rastreamento GPS neste dia", {
                          date: dayRange.label,
                        })
                  }
                  onClick={() => handleToggleDay(dayRange.key)}
                >
                  {dayRange.shortLabel}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      </>
      ) : null}

      {!embedded ? (
      <div className="field-app-tracking-legend-wrap">
        <ul className="list-unstyled field-app-tracking-legend">
          <li>
            <i className="line red-border"></i>
            {t("Acima da velocidade")}
          </li>
          <li>
            <i className="line blue-border"></i>
            {t("Dentro da velocidade")}
          </li>
          <li>
            <i className="line yellow-border"></i>
            {t("Abaixo da velocidade")}
          </li>
        </ul>

        <div className="field-app-speed-ref-control">
          <button
            type="button"
            className={`field-app-speed-ref-btn${
              speedEditorOpen ? " is-active" : ""
            }`}
            title={t("Alterar velocidade de referência")}
            aria-label={t("Alterar velocidade de referência")}
            onClick={() => setSpeedEditorOpen((open) => !open)}
          >
            <FaTachometerAlt />
          </button>
          <span className="field-app-speed-ref-value">
            {t("Referência")}: <strong>{plannedSpeedKmh} km/h</strong>
          </span>
          {speedEditorOpen ? (
            <div className="field-app-speed-ref-panel">
              <label htmlFor="field-app-planned-speed">
                {t("Velocidade de referência (km/h)")}
              </label>
              <input
                id="field-app-planned-speed"
                type="number"
                min="1"
                max="120"
                step="0.5"
                value={speedDraft}
                onChange={(event) => setSpeedDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") applyPlannedSpeed();
                }}
              />
              <button
                type="button"
                className="btn btn-sm btn-success"
                onClick={applyPlannedSpeed}
              >
                {t("Aplicar")}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-default"
                onClick={handleUseAverageSpeed}
                disabled={!summary.avgSpeedKmh}
              >
                {t("Usar média")} ({summary.avgSpeedKmh || 0})
              </button>
              <button
                type="button"
                className="btn btn-sm btn-default"
                onClick={handleUseMaxSpeed}
                disabled={!summary.maxSpeedKmh}
              >
                {t("Usar máxima")} ({summary.maxSpeedKmh || 0})
              </button>
            </div>
          ) : null}
        </div>
      </div>
      ) : null}

      <div className="field-app-tracking-map-chart-stack">
        <div
          ref={mapSectionRef}
          className="map-container field-app-tracking-map field-app-tracking-map--resizable"
          style={{
            "--field-app-map-height": `${
              embedded ? Math.min(mapHeightPx, 260) : mapHeightPx
            }px`,
          }}
        >
          <MapContainer
            map={mapState}
            map_salt={`${plannedSpeedKmh}-${selectedDayKeys.join(",")}-${mapRangeStartMs || 0}-${mapRangeEndMs || 0}-${mapPathPoints.length}-${timezoneMode}-${mapFullDayPath ? "full" : "window"}-${selectedLaneType || "brush"}-${highlightMapPoint ? `${highlightMapPoint.lat},${highlightMapPoint.lng}` : ""}-${operatorMarkerPosition ? `${operatorMarkerPosition.lat},${operatorMarkerPosition.lng}` : "nomarker"}`}
            farm_id={farmId}
            equipment={mapEquipment}
            livePosition={mapFullDayPath ? null : operatorMarkerPosition}
            landplots={landplots}
            directionMarkerClearToken={directionMarkerClearToken}
            onPathPointSelected={handlePathPointSelected}
            weatherHtmlForPoint={weatherHtmlForPoint}
            formatTraceTime={formatTraceTime}
            fitTrackingBounds={!embedded || !highlightMapPoint}
            highlightMapPoint={highlightMapPoint}
            trackingMapZoom={embedded ? embeddedSampleMapZoom : undefined}
            t={t}
          />
          <FieldAppTrackingWeatherHud
            series={weatherSeries}
            fallbackSeries={weatherFallbackSeries}
            maxAgeMs={WEATHER_LOOKUP_MAX_MS}
            timeMs={
              weatherTimeMs(
                highlightMapPoint &&
                  (highlightMapPoint.time || highlightMapPoint.iso)
              ) ||
              selectedTrackTimeMs ||
              weatherTimeMs(closureChartPoint && closureChartPoint.time) ||
              visibleGraphRange.endTimeMs ||
              weatherTimeMs(
                mapPathPoints.length
                  ? mapPathPoints[mapPathPoints.length - 1].time
                  : null
              )
            }
          />
          {!embedded ? (
          <button
            type="button"
            className="field-app-tracking-map-resize-handle"
            aria-label={t("Arraste para redimensionar o mapa")}
            title={t("Arraste para redimensionar o mapa")}
            onMouseDown={beginMapResize}
            onTouchStart={beginMapResize}
          />
          ) : null}
        </div>

        {!combineTrails ? (
        <div className="field-app-tracking-chart-block field-app-tracking-chart-block--with-lanes">
          {closureChartPickActive ? (
            <p className="field-app-activity-closure-chart-banner">
              {t(
                "Modo seleção: clique no gráfico no instante em que a máquina parou (ex.: último ponto com velocidade)."
              )}
            </p>
          ) : null}
          {chartGraphData.length > 0 ? (
            <LineGraph
              data={chartGraphData}
              period={[
                queryTimeBounds.startMs,
                queryTimeBounds.endMs,
              ]}
              refValue={plannedSpeedKmh}
              onClick={handleGraphClick}
              onRangeChange={handleGraphRangeChange}
              focusBrushRange={focusBrushRange}
              activityLanes={activityLanes}
              showActivityBand
              brushOnlyZoom
              onActivityIntervalClick={handleActivityIntervalClick}
              formatChartTime={formatChartTime}
              telemetryMarkers={telemetryMarkers}
              onTelemetryMarkerClick={handleTelemetryMarkerClick}
            />
          ) : null}

          {selectedActivityInterval ? (
            <FieldAppActivitySessionDetail
              selection={selectedActivityInterval}
              formatTimestamp={formatTrackingTimestamp}
              onClose={() => {
                setSelectedActivityInterval(null);
                setClosureChartPickActive(false);
                setClosureChartPoint(null);
              }}
              farmId={farmId}
              operatorIds={(selection && selection.operatorIds) || []}
              fuelRecords={fuelRecords}
              defaultSinceDay={
                selection && selection.dateFrom
                  ? dayKeyFromIso(selection.dateFrom)
                  : ""
              }
              chartPickActive={closureChartPickActive}
              onStartChartPick={handleStartClosureChartPick}
              onStopChartPick={handleStopClosureChartPick}
              selectedChartPoint={closureChartPoint}
              onClearChartPoint={handleClearClosureChartPoint}
            />
          ) : null}

          {activityLanes.length ? (
            <div className="field-app-graph-activity-legend">
              <span>
                <i
                  className="field-app-graph-activity-legend-swatch"
                  style={{ backgroundColor: "#1ABB9C" }}
                />
                {t("Telemetria (tempo total à direita)")}
              </span>
              <span>
                <i
                  className="field-app-graph-activity-legend-swatch"
                  style={{ backgroundColor: "#3498DB" }}
                />
                {t("Máquina assumida (nome + tempo total à direita)")}
              </span>
              {!hasActivityData ? (
                <span className="field-app-graph-activity-legend-note">
                  {t(
                    "Sem eventos de telemetria/máquina no App — faixas vazias neste período."
                  )}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        ) : null}
      </div>

      {!combineTrails ? machineSchedulePanel : null}

      {!embedded && !combineTrails ? (
      <>
      <div className="row">
        <CardContent
          title={t("Resumo do período visível no gráfico")}
          description={t(
            "Trecho visível no gráfico. Paradas menores que 5 min contam como movimento; paradas de 5 min ou mais contam desde o instante em que parou."
          )}
          borderColor="#28a745"
          data={periodCardData}
        />
        <CardContent
          title={t("Resumo total do intervalo filtrado")}
          description={t(
            "Todos os pontos GPS encontrados entre as datas escolhidas no App de Campo."
          )}
          borderColor="#007bff"
          data={totalCardData}
        />
      </div>

      <FieldAppServiceOrdersSection
        farmId={farmId}
        selection={selection}
        chartGraphData={chartGraphData}
        onFocusGraphPeriod={handleFocusGraphPeriod}
        t={t}
      />
      </>
      ) : null}
    </div>
  );
}
